import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { LargeTitleHeader } from '@/components/common';
import { getVendor, getVendorExpenses, updateVendor, type Vendor, type VendorExpense, type VendorExpenseSummary } from '@/lib/api/vendors';
import apiClient from '@/lib/api/client';
import { createExpense, updateExpense, EXPENSE_CATEGORIES } from '@/lib/api/expenses';
import { getJobs, type Job } from '@/lib/api/jobs';
import { jobSectionPath } from '@/lib/utils/navPaths';
import { useToast } from '@/lib/hooks/useToast';
import { Link } from 'react-router-dom';

const money = (n: number) =>
  `$${(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (d: string | null) =>
  d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

// A mail sender that plausibly matches this vendor (CR #109)
interface MailCandidate { id: number; email: string; name: string; msg_count: number; domain_match: boolean }

// Vendor profile (CR #84): contact card + payables ledger — log expenses against
// this vendor, see what's paid vs pending, and chase due dates.
export function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [expenses, setExpenses] = useState<VendorExpense[]>([]);
  const [summary, setSummary] = useState<VendorExpenseSummary | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Log-expense form state
  const today = new Date().toISOString().slice(0, 10);
  const [fJob, setFJob] = useState('');
  const [fCategory, setFCategory] = useState('materials');
  const [fDate, setFDate] = useState(today);
  const [fTotal, setFTotal] = useState('');
  const [fInvoice, setFInvoice] = useState('');
  const [fPaid, setFPaid] = useState(false);
  const [fDue, setFDue] = useState('');
  const [fDesc, setFDesc] = useState('');

  // CR #109 — "Find in mail" contact enrichment (only offered when no email on file)
  const [finderOpen, setFinderOpen] = useState(false);
  const [finderLoading, setFinderLoading] = useState(false);
  const [candidates, setCandidates] = useState<MailCandidate[] | null>(null);
  const [manualEmail, setManualEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      const [v, ex] = await Promise.all([getVendor(Number(id)), getVendorExpenses(Number(id))]);
      setVendor(v);
      setExpenses(ex.expenses);
      setSummary(ex.summary);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    getJobs({ limit: 200 }).then(r => setJobs(r.jobs)).catch(() => {});
  }, []);

  const submitExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vendor) return;
    if (!fJob) { toast.error('Pick the job this expense belongs to'); return; }
    if (!fTotal || isNaN(Number(fTotal))) { toast.error('Enter the total amount'); return; }
    if (!fPaid && !fDue) { toast.error('Pending expense — set the expected due date'); return; }
    setSaving(true);
    try {
      await createExpense(fJob, {
        vendor: vendor.name,
        vendor_id: vendor.id,
        category: fCategory,
        expense_date: fDate || null,
        total: Number(fTotal),
        description: fDesc.trim() || null,
        paid: fPaid,
        due_date: fPaid ? null : fDue,
        invoice_number: fInvoice.trim() || null,
      });
      toast.success('Expense logged');
      setFTotal(''); setFInvoice(''); setFDesc(''); setFDue(''); setFPaid(false);
      setShowForm(false);
      load();
    } catch {
      toast.error('Failed to log expense');
    } finally {
      setSaving(false);
    }
  };

  // CR #109 — query the mail store for senders matching this vendor's name.
  // Nothing is ever written without an explicit "Use this" / Save click.
  const findInMail = async () => {
    if (!vendor) return;
    setFinderOpen(true);
    setFinderLoading(true);
    try {
      const r = await apiClient.get(`/mail/vendor-candidates?name=${encodeURIComponent(vendor.name)}`);
      setCandidates(r.data.data ?? []);
    } catch {
      setCandidates([]);
    } finally {
      setFinderLoading(false);
    }
  };

  const saveVendorEmail = async (addr: string) => {
    if (!vendor) return;
    const email = addr.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('That does not look like an email address'); return; }
    setSavingEmail(true);
    try {
      // PUT replaces all fields, so send the full record with the new email
      await updateVendor(vendor.id, {
        name: vendor.name, contact: vendor.contact, phone: vendor.phone,
        email, address: vendor.address, website: vendor.website, notes: vendor.notes,
      });
      toast.success(`Saved ${email} to ${vendor.name}`);
      setFinderOpen(false);
      setCandidates(null);
      setManualEmail('');
      load();
    } catch {
      toast.error('Failed to save the email');
    } finally {
      setSavingEmail(false);
    }
  };

  const markPaid = async (ex: VendorExpense) => {
    setBusyId(ex.id);
    try {
      await updateExpense(ex.job_id, ex.id, { paid: true });
      toast.success('Marked paid');
      load();
    } catch {
      toast.error('Failed to update');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="h-40 flex items-center justify-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-gray-300">progress_activity</span>
        </div>
      </MainLayout>
    );
  }

  if (notFound || !vendor) {
    return (
      <MainLayout>
        <div className="p-8 text-center">
          <p className="text-gray-500 mb-3">Vendor not found.</p>
          <button onClick={() => navigate('/vendors')} className="text-primary font-medium hover:underline">Back to vendors</button>
        </div>
      </MainLayout>
    );
  }

  const pending = expenses.filter(ex => !ex.paid);
  const paid = expenses.filter(ex => ex.paid);

  return (
    <MainLayout>
      <LargeTitleHeader
        title={vendor.name}
        subtitle={vendor.contact || 'Vendor profile'}
        onBack={() => navigate('/vendors')}
        backLabel="Vendors"
        actions={
          <button
            onClick={() => setShowForm(s => !s)}
            className="flex h-9 items-center justify-center gap-1.5 rounded-lg px-3 bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">{showForm ? 'close' : 'add'}</span>
            <span className="hidden sm:inline">{showForm ? 'Cancel' : 'Log expense'}</span>
          </button>
        }
      />

      {/* Summary tiles */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Total spend</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{money(summary.total)}</p>
            <p className="text-[11px] text-gray-400">{summary.count} expense{summary.count === 1 ? '' : 's'}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Paid</p>
            <p className="text-xl font-bold text-success mt-1">{money(summary.paid_total)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Pending</p>
            <p className={`text-xl font-bold mt-1 ${summary.unpaid_total > 0 ? 'text-warning' : 'text-gray-900'}`}>{money(summary.unpaid_total)}</p>
            <p className="text-[11px] text-gray-400">{summary.unpaid_count} open</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <p className="text-xs text-gray-500">Overdue</p>
            <p className={`text-xl font-bold mt-1 ${summary.overdue_count > 0 ? 'text-critical' : 'text-gray-900'}`}>{summary.overdue_count}</p>
            <p className="text-[11px] text-gray-400">past due date</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Log expense form */}
          {showForm && (
            <form onSubmit={submitExpense} className="bg-white rounded-xl border border-gray-200 p-5 space-y-3">
              <h3 className="text-base font-bold text-gray-900">Log an expense with {vendor.name}</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Job</span>
                  <select value={fJob} onChange={e => setFJob(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
                    <option value="">Select job…</option>
                    {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Category</span>
                  <select value={fCategory} onChange={e => setFCategory(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm capitalize">
                    {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                  </select>
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Date</span>
                  <input type="date" value={fDate} onChange={e => setFDate(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Total ($)</span>
                  <input type="number" step="0.01" min="0" value={fTotal} onChange={e => setFTotal(e.target.value)} placeholder="0.00" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
                </label>
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">Invoice # (optional)</span>
                  <input value={fInvoice} onChange={e => setFInvoice(e.target.value)} placeholder="INV-1234" className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
                </label>
                <div className="flex items-end gap-3">
                  <label className="flex items-center gap-2 h-9">
                    <input type="checkbox" checked={fPaid} onChange={e => setFPaid(e.target.checked)} className="accent-[#C77D3F]" />
                    <span className="text-sm text-gray-700">Already paid</span>
                  </label>
                  {!fPaid && (
                    <label className="flex flex-col gap-1 flex-1">
                      <span className="text-xs font-medium text-gray-500">Expected due date</span>
                      <input type="date" value={fDue} onChange={e => setFDue(e.target.value)} className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
                    </label>
                  )}
                </div>
              </div>
              <input value={fDesc} onChange={e => setFDesc(e.target.value)} placeholder="Description (optional)" className="w-full h-9 px-3 rounded-lg border border-gray-300 text-sm" />
              <div className="flex justify-end">
                <button type="submit" disabled={saving} className="h-9 px-4 rounded-lg bg-primary text-white text-sm font-semibold hover:opacity-90 disabled:opacity-60">
                  {saving ? 'Saving…' : 'Log expense'}
                </button>
              </div>
            </form>
          )}

          {/* Pending payables */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900">Pending payment</h3>
              {pending.length > 0 && (
                <span className="text-xs font-semibold text-warning bg-warning/10 rounded-full px-2 py-0.5">{money(pending.reduce((s, ex) => s + ex.total, 0))} owing</span>
              )}
            </div>
            {pending.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400">Nothing pending — all square with {vendor.name}.</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {pending.map(ex => (
                  <li key={ex.id} className="px-5 py-3 flex flex-wrap items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {ex.invoice_number ? `${ex.invoice_number} · ` : ''}{ex.description || ex.category.replace('_', ' ')}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {ex.job_title ? (
                          <Link to={jobSectionPath(ex.client_id ?? undefined, ex.job_id, 'expenses')} className="hover:underline text-gray-500">
                            {ex.job_number} · {ex.job_title}
                          </Link>
                        ) : 'No job'}
                        {ex.expense_date ? ` — ${fmtDate(ex.expense_date)}` : ''}
                      </p>
                    </div>
                    <span className={`text-xs font-semibold whitespace-nowrap ${ex.overdue ? 'text-critical' : 'text-gray-500'}`}>
                      {ex.overdue ? 'OVERDUE ' : 'due '}{fmtDate(ex.due_date)}
                    </span>
                    <span className="text-sm font-bold text-gray-900 whitespace-nowrap">{money(ex.total)}</span>
                    <button
                      onClick={() => markPaid(ex)}
                      disabled={busyId === ex.id}
                      className="h-8 px-2.5 rounded-lg border border-success/40 text-success text-xs font-semibold hover:bg-success/10 disabled:opacity-50 whitespace-nowrap"
                    >
                      {busyId === ex.id ? '…' : 'Mark paid'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Paid history */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-100">
              <h3 className="text-sm font-bold text-gray-900">Payment history</h3>
            </div>
            {paid.length === 0 ? (
              <p className="px-5 py-6 text-sm text-gray-400">No paid expenses recorded yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[480px]">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50/60">
                      <th className="px-5 py-2 font-semibold">Expense</th>
                      <th className="px-2 py-2 font-semibold">Job</th>
                      <th className="px-2 py-2 font-semibold whitespace-nowrap">Date</th>
                      <th className="px-5 py-2 font-semibold text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {paid.map(ex => (
                      <tr key={ex.id} className="hover:bg-gray-50">
                        <td className="px-5 py-2.5">
                          <p className="font-medium text-gray-900 truncate max-w-[16rem]">
                            {ex.invoice_number ? `${ex.invoice_number} · ` : ''}{ex.description || ex.category.replace('_', ' ')}
                          </p>
                        </td>
                        <td className="px-2 py-2.5 text-xs text-gray-500 truncate max-w-[10rem]">
                          {ex.job_title ? (
                            <Link to={jobSectionPath(ex.client_id ?? undefined, ex.job_id, 'expenses')} className="hover:underline">{ex.job_title}</Link>
                          ) : '—'}
                        </td>
                        <td className="px-2 py-2.5 text-xs text-gray-500 whitespace-nowrap">{fmtDate(ex.expense_date)}</td>
                        <td className="px-5 py-2.5 text-right font-semibold text-gray-900 whitespace-nowrap">{money(ex.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Contact card */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 h-fit">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Contact</h3>
          <dl className="space-y-2.5 text-sm">
            {vendor.contact && (<div><dt className="text-xs text-gray-400">Contact person</dt><dd className="text-gray-800">{vendor.contact}</dd></div>)}
            {vendor.phone && (<div><dt className="text-xs text-gray-400">Phone</dt><dd><a href={`tel:${vendor.phone}`} className="text-primary hover:underline">{vendor.phone}</a></dd></div>)}
            {vendor.email && (<div><dt className="text-xs text-gray-400">Email</dt><dd><a href={`mailto:${vendor.email}`} className="text-primary hover:underline break-all">{vendor.email}</a></dd></div>)}
            {vendor.address && (<div><dt className="text-xs text-gray-400">Address</dt><dd className="text-gray-800">{vendor.address}</dd></div>)}
            {vendor.website && (<div><dt className="text-xs text-gray-400">Website</dt><dd><a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noreferrer" className="text-primary hover:underline break-all">{vendor.website}</a></dd></div>)}
            {vendor.notes && (<div><dt className="text-xs text-gray-400">Notes</dt><dd className="text-gray-600 whitespace-pre-wrap">{vendor.notes}</dd></div>)}
            {!vendor.contact && !vendor.phone && !vendor.email && !vendor.address && !vendor.website && !vendor.notes && (
              <p className="text-gray-400 text-sm">No contact details on file — edit this vendor from the vendors list.</p>
            )}
          </dl>

          {/* CR #109 — no email on file: offer to find one in the mailbox */}
          {!vendor.email && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              {!finderOpen ? (
                <button onClick={findInMail}
                  className="flex items-center gap-1.5 text-xs font-bold text-primary hover:underline">
                  <span className="material-symbols-outlined text-base">travel_explore</span>
                  Find email in mail
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-700">Matches in your mailbox</p>
                    <button onClick={() => setFinderOpen(false)} className="text-xs text-gray-400 hover:text-gray-600">Close</button>
                  </div>
                  {finderLoading ? (
                    <p className="text-xs text-gray-400">Searching senders matching “{vendor.name}”…</p>
                  ) : !candidates || candidates.length === 0 ? (
                    <p className="text-xs text-gray-400">No senders in the mailbox plausibly match “{vendor.name}”.</p>
                  ) : (
                    <ul className="space-y-1.5">
                      {candidates.map(c => (
                        <li key={c.id} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{c.email}</p>
                            <p className="text-[10px] text-gray-400 truncate">
                              {c.name || 'no display name'} · {c.msg_count} msg{Number(c.msg_count) === 1 ? '' : 's'}
                              {c.domain_match ? ' · domain match' : ''}
                            </p>
                          </div>
                          <button onClick={() => saveVendorEmail(c.email)} disabled={savingEmail}
                            className="h-7 px-2 rounded-lg border border-primary/40 text-primary text-[11px] font-bold hover:bg-primary/5 disabled:opacity-40 whitespace-nowrap">
                            Use this
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex items-center gap-2 pt-1">
                    <input value={manualEmail} onChange={e => setManualEmail(e.target.value)}
                      placeholder="…or type an email"
                      className="flex-1 h-8 px-2 rounded-lg border border-gray-200 text-xs" />
                    <button onClick={() => saveVendorEmail(manualEmail)} disabled={savingEmail || !manualEmail.trim()}
                      className="h-8 px-3 rounded-lg bg-primary text-white text-[11px] font-bold disabled:opacity-40">
                      Save
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
